import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ArrowLeft, Save, Trash2, FileText, Eye, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { confirm } from "@/lib/confirm";

const DOC_TYPES = [
  {
    key: "boletim_operatorio",
    label: "Boletim Operatório",
    body_field: "boletim_operative_description",
    body_label: "Descrição do ato operatório",
    placeholder: "Ex: Anestesia geral. Antissepsia e assepsia. Incisão subcostal direita. Dissecção do trígono de Calot com identificação de artéria e ducto císticos. Ligadura dupla e secção. Dissecção da vesícula do leito hepático. Revisão de hemostasia. Síntese por planos.",
    rows: 14,
    help: "Texto padrão que aparecerá no campo \"DESCRIÇÃO DO ATO OPERATÓRIO\" do Boletim Operatório.",
  },
  {
    key: "prescricao_medica",
    label: "Prescrição Médica",
    body_field: "prescricao_body",
    body_label: "Prescrição padrão",
    placeholder: "1. Dieta conforme tolerância...\n2. Soro fisiológico 0,9% 1000ml EV em 8h.\n3. Dipirona 1g EV 6/6h SOS dor/febre...",
    rows: 14,
    help: "Lista de itens da prescrição que aparecerá na Prescrição Médica Diária.",
  },
  {
    key: "aih",
    label: "AIH",
    body_field: "aih_justification",
    body_label: "Justificativa / Sinais, sintomas e condições que justificam a internação",
    placeholder: "Paciente portadora de colelitíase sintomática, com episódios recorrentes de cólica biliar, com indicação de colecistectomia eletiva para evitar complicações.",
    rows: 10,
    help: "Texto que aparecerá no campo \"JUSTIFICATIVA DA INTERNAÇÃO\" do AIH. Os CIDs e o código SUS também são usados.",
  },
  {
    key: "histopatologico",
    label: "Histopatológico",
    body_field: "histopatologico_clinical_history",
    body_label: "História clínica e exames complementares",
    placeholder: "Paciente com quadro de dor em hipocôndrio direito, com USG de abdome evidenciando colelitíase múltipla. Sem icterícia, sem pancreatite prévia. Enviado material de colecistectomia para análise histopatológica.",
    rows: 10,
    help: "Aparece na \"HISTÓRIA CLÍNICA E EXAMES COMPLEMENTARES\" da requisição de Histopatológico.",
  },
  {
    key: "tcle",
    label: "TCLE",
    body_field: "tcle_body",
    body_label: "Texto do Termo de Consentimento",
    placeholder: "Declaro que fui informado(a) de forma clara e detalhada pelo(a) médico(a) responsável sobre o procedimento cirúrgico a que serei submetido(a), suas indicações, técnicas, riscos, complicações possíveis...",
    rows: 18,
    help: "Corpo do TCLE. Se vazio, será usado o texto padrão genérico da BV Serviços Médicos.",
  },
];

export default function ProcedureDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [proc, setProc] = useState(null);
  const [saving, setSaving] = useState(false);
  const [globalRates, setGlobalRates] = useState({ bvsm: 0.35, anesth: 0.25 });
  // Raw textarea/input strings for comma-separated fields. Editing the array
  // directly broke typing (commas were stripped on every keystroke because
  // `split(",").filter(Boolean)` discards the empty token after the comma).
  const [aliasesText, setAliasesText] = useState("");
  const [cidText, setCidText] = useState("");

  function _csvToList(s) {
    return (s || "").split(",").map((x) => x.trim()).filter(Boolean);
  }

  // Snapshot dos valores financeiros no momento do load — usado para detectar
  // mudanças quando o usuário clica em Salvar (e exibir o dialog de cascade).
  const originalFinancialsRef = useRef(null);
  const [cascadeDialog, setCascadeDialog] = useState({
    open: false,
    scope: "future",
    fromDate: new Date().toISOString().slice(0, 10),
    pendingPayload: null,
  });

  async function load() {
    try {
      const r = await api.get(`/procedures/${id}`);
      const data = { ...r.data, document_bodies: r.data.document_bodies || {}, cid_codes: r.data.cid_codes || [], required_documents: r.data.required_documents || [] };
      setProc(data);
      setAliasesText((data.aliases || []).join(", "));
      setCidText((data.cid_codes || []).join(", "));
      originalFinancialsRef.current = {
        valor_bruto: Number(data.valor_bruto) || 0,
        bvsm_retention_rate: data.bvsm_retention_rate ?? null,
        anesthesiologist_share_rate: data.anesthesiologist_share_rate ?? null,
      };
      try {
        const s = await api.get("/settings");
        setGlobalRates({
          bvsm: Number(s.data?.bvsm_retention_rate ?? 0.35),
          anesth: Number(s.data?.anesthesiologist_share_rate ?? 0.25),
        });
      } catch { /* settings is optional for the simulation panel */ }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  function _buildPayload() {
    return {
      name: proc.name,
      sus_code: proc.sus_code,
      cid_codes: _csvToList(cidText),
      specialty: proc.specialty,
      default_diagnosis: proc.default_diagnosis,
      aliases: _csvToList(aliasesText),
      default_operative_description: proc.default_operative_description,
      default_prescription: proc.default_prescription,
      required_documents: proc.required_documents,
      document_bodies: proc.document_bodies,
      active: proc.active,
      valor_bruto: Number(proc.valor_bruto) || 0,
      bvsm_retention_rate: proc.bvsm_retention_rate === "" || proc.bvsm_retention_rate == null
        ? null
        : Number(proc.bvsm_retention_rate),
      anesthesiologist_share_rate: proc.anesthesiologist_share_rate === "" || proc.anesthesiologist_share_rate == null
        ? null
        : Number(proc.anesthesiologist_share_rate),
      pro_bono: !!proc.pro_bono,
    };
  }

  function _financialsChanged(payload) {
    const orig = originalFinancialsRef.current || {};
    return (
      Number(payload.valor_bruto || 0) !== Number(orig.valor_bruto || 0)
      || (payload.bvsm_retention_rate ?? null) !== (orig.bvsm_retention_rate ?? null)
      || (payload.anesthesiologist_share_rate ?? null) !== (orig.anesthesiologist_share_rate ?? null)
    );
  }

  async function save() {
    const payload = _buildPayload();
    // If a financial rule changed, ask the user about retroactive cascade.
    if (_financialsChanged(payload)) {
      setCascadeDialog({
        open: true,
        scope: "future",
        fromDate: new Date().toISOString().slice(0, 10),
        pendingPayload: payload,
      });
      return;
    }
    await _doSave(payload, { scope: "none" });
  }

  async function _doSave(payload, cascade) {
    setSaving(true);
    try {
      const qs = new URLSearchParams();
      if (cascade?.scope && cascade.scope !== "none") {
        qs.set("cascade_scope", cascade.scope);
        if (cascade.scope === "from_date" && cascade.fromDate) {
          qs.set("cascade_from_date", cascade.fromDate);
        }
      }
      const url = `/procedures/${id}${qs.toString() ? `?${qs.toString()}` : ""}`;
      const r = await api.patch(url, payload);
      const updated = r.data?.cascade?.updated_count;
      const rematch = r.data?.rematch || {};
      const pbProp = r.data?.pro_bono_propagation || {};
      const extras = [];
      if (rematch.rows_matched) {
        extras.push(`${rematch.rows_matched} linha${rematch.rows_matched === 1 ? "" : "s"} do mapa reconhecida${rematch.rows_matched === 1 ? "" : "s"} retroativamente`);
      }
      if (rematch.cases_synced) {
        extras.push(`${rematch.cases_synced} caso${rematch.cases_synced === 1 ? "" : "s"} sincronizado${rematch.cases_synced === 1 ? "" : "s"}`);
      }
      if (pbProp.cases_marked) {
        extras.push(`${pbProp.cases_marked} caso${pbProp.cases_marked === 1 ? "" : "s"} marcado${pbProp.cases_marked === 1 ? "" : "s"} como Pro-Bono`);
      }
      const extraTxt = extras.length ? ` · ${extras.join(" · ")}` : "";
      if (cascade?.scope && cascade.scope !== "none" && typeof updated === "number") {
        toast.success(
          updated > 0
            ? `Procedimento salvo · ${updated} cirurgia${updated === 1 ? "" : "s"} atualizada${updated === 1 ? "" : "s"}${extraTxt}`
            : `Procedimento salvo (nenhuma cirurgia se enquadrou no escopo escolhido)${extraTxt}`,
        );
      } else {
        toast.success(`Procedimento salvo${extraTxt}`);
      }
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  }

  async function _confirmCascade() {
    const { pendingPayload, scope, fromDate } = cascadeDialog;
    if (scope === "from_date" && !fromDate) {
      toast.error("Selecione a data inicial.");
      return;
    }
    setCascadeDialog((s) => ({ ...s, open: false, pendingPayload: null }));
    await _doSave(pendingPayload, { scope, fromDate });
  }

  async function remove() {
    if (!(await confirm({ description: `Excluir "${proc.name}"?` }))) return;
    try {
      await api.delete(`/procedures/${id}`);
      toast.success("Procedimento excluído");
      nav("/procedimentos");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  function updateField(k, v) { setProc({ ...proc, [k]: v }); }
  function updateBody(field, v) { setProc({ ...proc, document_bodies: { ...(proc.document_bodies || {}), [field]: v } }); }
  function toggleRequiredDoc(key) {
    const curr = proc.required_documents || [];
    const next = curr.includes(key) ? curr.filter((x) => x !== key) : [...curr, key];
    setProc({ ...proc, required_documents: next });
  }

  if (!proc) return <div className="text-slate-500">Carregando...</div>;

  return (
    <div className="space-y-6" data-testid="procedure-detail">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <Button variant="ghost" size="sm" onClick={() => nav("/procedimentos")} data-testid="btn-back">
          <ArrowLeft className="h-4 w-4 mr-1" /> Procedimentos
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" className="text-red-600 hover:bg-red-50 border-red-200" onClick={remove} data-testid="btn-delete">
            <Trash2 className="h-4 w-4 mr-1" /> Excluir
          </Button>
          <Button className="bg-blue-700 hover:bg-blue-800" onClick={save} disabled={saving} data-testid="btn-save-procedure">
            <Save className="h-4 w-4 mr-1" /> {saving ? "Salvando..." : "Salvar alterações"}
          </Button>
        </div>
      </div>

      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">{proc.name || "Procedimento"}</h1>
        <p className="text-sm text-slate-500 mt-1">{proc.specialty || "Sem especialidade"} · SUS {proc.sus_code || "—"}</p>
      </div>

      <Tabs defaultValue="dados">
        <TabsList className="bg-white border border-slate-200 flex-wrap">
          <TabsTrigger value="dados" data-testid="tab-dados">Dados Gerais</TabsTrigger>
          <TabsTrigger value="financeiro" data-testid="tab-financeiro">Financeiro</TabsTrigger>
          <TabsTrigger value="documentos" data-testid="tab-documentos">Documentos Obrigatórios</TabsTrigger>
          {DOC_TYPES.map((d) => (
            <TabsTrigger key={d.key} value={d.key} data-testid={`tab-${d.key}`}>{d.label}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="dados">
          <Card><CardContent className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Nome do procedimento *</Label>
              <Input value={proc.name || ""} onChange={(e) => updateField("name", e.target.value)} data-testid="input-name" /></div>
            <div className="space-y-1.5"><Label>Especialidade</Label>
              <Input value={proc.specialty || ""} onChange={(e) => updateField("specialty", e.target.value)} data-testid="input-specialty" /></div>
            <div className="space-y-1.5"><Label>Código SUS</Label>
              <Input value={proc.sus_code || ""} onChange={(e) => updateField("sus_code", e.target.value)} placeholder="ex: 04.07.02.037-3" data-testid="input-sus" /></div>
            <div className="space-y-1.5"><Label>CID-10 (separe por vírgula)</Label>
              <Input value={cidText}
                onChange={(e) => setCidText(e.target.value)}
                placeholder="K80.0, K81.1" data-testid="input-cid" /></div>
            <div className="md:col-span-2 space-y-1.5"><Label>Diagnóstico padrão</Label>
              <Textarea rows={3} value={proc.default_diagnosis || ""} onChange={(e) => updateField("default_diagnosis", e.target.value)}
                placeholder="Ex: Colelitíase sintomática" data-testid="input-diagnosis" /></div>

            <div className="md:col-span-2 space-y-1.5">
              <Label>Palavras-chave para reconhecimento no mapa (separe por vírgula)</Label>
              <Textarea
                rows={2}
                value={aliasesText}
                onChange={(e) => setAliasesText(e.target.value)}
                placeholder="Ex: colecistectomia, cole, vesícula, colecistec"
                data-testid="input-aliases"
              />
              <p className="text-[11px] text-slate-500">
                O parser do mapa reconhece este procedimento se encontrar <strong>qualquer uma destas palavras</strong> no texto
                (ignora qualificadores como "pós CPRE", "por pancreatite" etc). Toda variação que você adicionar aqui passa
                a remeter automaticamente para <strong>{proc?.name || "este procedimento"}</strong> no mapa filtrado e nos casos.
                Se vazio, usa apenas o nome completo. Se duas palavras-chave de procedimentos diferentes baterem com o mesmo
                tamanho, o sistema abre alerta pra você escolher qual.
              </p>
            </div>
            <div className="md:col-span-2 flex items-center gap-2">
              <Checkbox id="active" checked={!!proc.active} onCheckedChange={(v) => updateField("active", !!v)} data-testid="chk-active" />
              <Label htmlFor="active">Ativo</Label>
            </div>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="financeiro">
          <Card><CardContent className="p-6 space-y-6">
            <div>
              <h3 className="font-display font-semibold text-slate-900">Regra financeira do procedimento</h3>
              <p className="text-sm text-slate-500 mt-1">
                Defina <strong>quanto a BVSM retém</strong> e <strong>quanto vai para o anestesista</strong>.
                O restante é dividido em partes iguais entre os 2 ou 3 cirurgiões do caso.
                Os percentuais ficam editáveis — os valores líquidos são <strong>calculados automaticamente</strong>.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label>Valor Bruto SUS (R$)</Label>
                <Input type="number" step="0.01" min="0"
                  value={proc.valor_bruto ?? 0}
                  onChange={(e) => updateField("valor_bruto", e.target.value)}
                  data-testid="input-valor-bruto" />
                <p className="text-[11px] text-slate-400">Valor total pago pelo SUS por este procedimento.</p>
              </div>
              <div className="space-y-1.5">
                <Label>% Administrativo BVSM</Label>
                <Input type="number" step="0.1" min="0" max="100"
                  value={proc.bvsm_retention_rate == null ? "" : (proc.bvsm_retention_rate * 100)}
                  placeholder={`${(globalRates.bvsm * 100).toFixed(1)} (padrão global)`}
                  onChange={(e) => {
                    const v = e.target.value;
                    updateField("bvsm_retention_rate", v === "" ? null : Number(v) / 100);
                  }}
                  data-testid="input-bvsm-pct" />
                <p className="text-[11px] text-slate-400">Vazio = usa o padrão das Configurações ({(globalRates.bvsm * 100).toFixed(1)}%).</p>
              </div>
              <div className="space-y-1.5">
                <Label>% Anestesista (sobre o restante)</Label>
                <Input type="number" step="0.1" min="0" max="100"
                  value={proc.anesthesiologist_share_rate == null ? "" : (proc.anesthesiologist_share_rate * 100)}
                  placeholder={`${(globalRates.anesth * 100).toFixed(1)} (padrão global)`}
                  onChange={(e) => {
                    const v = e.target.value;
                    updateField("anesthesiologist_share_rate", v === "" ? null : Number(v) / 100);
                  }}
                  data-testid="input-anesth-pct" />
                <p className="text-[11px] text-slate-400">Vazio = usa o padrão das Configurações ({(globalRates.anesth * 100).toFixed(1)}%).</p>
              </div>
            </div>

            {/* Simulação em tempo real */}
            <FinancialSimulation
              bruto={Number(proc.valor_bruto) || 0}
              bvsmRate={proc.bvsm_retention_rate ?? globalRates.bvsm}
              anesthRate={proc.anesthesiologist_share_rate ?? globalRates.anesth}
            />

            {/* Pro-Bono: marca o procedimento como 100% gratuito */}
            <div className="border-2 border-amber-200 bg-amber-50/40 rounded-md p-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4"
                  checked={!!proc.pro_bono}
                  onChange={(e) => updateField("pro_bono", e.target.checked)}
                  data-testid="chk-procedure-pro-bono"
                />
                <div className="flex-1">
                  <div className="font-medium text-amber-900">Sempre Pro-Bono (100% voluntário)</div>
                  <div className="text-xs text-amber-800 mt-1">
                    Marca todos os casos deste procedimento como pro-bono automaticamente. Esses casos contam na Produção mas <strong>não geram receita</strong> (são excluídos do Financeiro, Faturamento e Controle BVSM).
                    Útil para procedimentos historicamente doados (vasectomia social, etc).
                  </div>
                  {proc.pro_bono && (
                    <div className="text-[11px] text-amber-700 mt-2 italic">
                      Ao salvar, os casos existentes que usam este procedimento também serão marcados como Pro-Bono retroativamente.
                    </div>
                  )}
                </div>
              </label>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs text-slate-600 leading-relaxed">
              <strong>Como o cálculo funciona:</strong> Valor bruto → BVSM retém o % administrativo. Sobre o que sobra
              (produção), o anestesista leva o % definido. O restante é a parte dos cirurgiões, dividida em partes iguais
              (1, 2 ou 3 cirurgiões por caso). Esta regra vale em todos os módulos: Financeiro, Controle BVSM e exports.
            </div>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="documentos">
          <Card><CardContent className="p-6 space-y-4">
            <div>
              <h3 className="font-display font-semibold text-slate-900">Documentos que devem ser gerados para este procedimento</h3>
              <p className="text-sm text-slate-500 mt-1">Marque quais documentos são obrigatórios. Ao abrir um caso deste procedimento, apenas esses aparecerão como opções.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {DOC_TYPES.map((d) => {
                const checked = (proc.required_documents || []).includes(d.key);
                return (
                  <label key={d.key} className={`flex items-start gap-3 p-4 border rounded-md cursor-pointer transition-colors ${checked ? "border-blue-400 bg-blue-50/40" : "border-slate-200 hover:border-slate-300"}`}
                    data-testid={`doc-toggle-${d.key}`}>
                    <Checkbox checked={checked} onCheckedChange={() => toggleRequiredDoc(d.key)} />
                    <div>
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-blue-700" />
                        <span className="font-medium text-slate-900">{d.label}</span>
                      </div>
                      <div className="text-xs text-slate-500 mt-1">{d.help}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </CardContent></Card>
        </TabsContent>

        {DOC_TYPES.map((d) => (
          <TabsContent key={d.key} value={d.key}>
            <Card><CardContent className="p-6 space-y-4">
              <div>
                <h3 className="font-display font-semibold text-slate-900">{d.label}</h3>
                <p className="text-sm text-slate-500 mt-1">{d.help}</p>
              </div>
              <div className="space-y-1.5">
                <Label>{d.body_label}</Label>
                <Textarea
                  rows={d.rows}
                  value={proc.document_bodies?.[d.body_field] ?? (d.key === "boletim_operatorio" ? (proc.default_operative_description || "") : d.key === "prescricao_medica" ? (proc.default_prescription || "") : "")}
                  onChange={(e) => updateBody(d.body_field, e.target.value)}
                  placeholder={d.placeholder}
                  className="font-mono text-sm"
                  data-testid={`textarea-${d.key}`}
                />
                <p className="text-xs text-slate-400">
                  Dicas: use quebras de linha para listas. Este texto será colocado dentro do documento PDF, preservando a formatação.
                </p>
              </div>
              {d.key === "boletim_operatorio" && (
                <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs text-slate-600">
                  Dica: este corpo também é acessível via o campo <strong>descrição operatória padrão</strong> (Dados Gerais).
                  Você pode deixá-lo vazio aqui e usar só o campo padrão — ou personalizar aqui por procedimento.
                </div>
              )}
              <div className="flex gap-2 pt-2">
                <Button className="bg-blue-700 hover:bg-blue-800" onClick={save} disabled={saving}>
                  <Save className="h-4 w-4 mr-1" /> {saving ? "Salvando..." : "Salvar"}
                </Button>
              </div>
            </CardContent></Card>
          </TabsContent>
        ))}
      </Tabs>

      {/* Dialog de cascade — escolha do escopo retroativo */}
      <Dialog open={cascadeDialog.open} onOpenChange={(open) => !open && setCascadeDialog((s) => ({ ...s, open: false, pendingPayload: null }))}>
        <DialogContent className="sm:max-w-lg" data-testid="cascade-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Aplicar alterações financeiras
            </DialogTitle>
            <DialogDescription>
              Você alterou valores financeiros deste procedimento. Em quais cirurgias as alterações devem valer?
            </DialogDescription>
          </DialogHeader>

          <RadioGroup
            value={cascadeDialog.scope}
            onValueChange={(v) => setCascadeDialog((s) => ({ ...s, scope: v }))}
            className="space-y-3 py-2"
          >
            <div className="flex items-start gap-3 rounded-md border border-slate-200 p-3 hover:bg-slate-50 cursor-pointer"
                 onClick={() => setCascadeDialog((s) => ({ ...s, scope: "future" }))}>
              <RadioGroupItem value="future" id="scope-future" data-testid="scope-future" className="mt-0.5" />
              <div className="flex-1">
                <Label htmlFor="scope-future" className="font-medium cursor-pointer">Apenas cirurgias FUTURAS</Label>
                <p className="text-xs text-slate-500 mt-0.5">Vale a partir de hoje. Cirurgias passadas mantêm os valores antigos.</p>
              </div>
            </div>

            <div className="flex items-start gap-3 rounded-md border border-slate-200 p-3 hover:bg-slate-50 cursor-pointer"
                 onClick={() => setCascadeDialog((s) => ({ ...s, scope: "all" }))}>
              <RadioGroupItem value="all" id="scope-all" data-testid="scope-all" className="mt-0.5" />
              <div className="flex-1">
                <Label htmlFor="scope-all" className="font-medium cursor-pointer">Sempre — TODAS as cirurgias existentes</Label>
                <p className="text-xs text-slate-500 mt-0.5">Recalcula o histórico completo de cirurgias com a regra nova. Atenção: sobrescreve valores já registrados.</p>
              </div>
            </div>

            <div className="flex items-start gap-3 rounded-md border border-slate-200 p-3 hover:bg-slate-50 cursor-pointer"
                 onClick={() => setCascadeDialog((s) => ({ ...s, scope: "from_date" }))}>
              <RadioGroupItem value="from_date" id="scope-from-date" data-testid="scope-from-date" className="mt-0.5" />
              <div className="flex-1 space-y-2">
                <Label htmlFor="scope-from-date" className="font-medium cursor-pointer">A partir de uma data específica</Label>
                <Input
                  type="date"
                  value={cascadeDialog.fromDate}
                  onChange={(e) => setCascadeDialog((s) => ({ ...s, fromDate: e.target.value, scope: "from_date" }))}
                  className="w-44"
                  data-testid="scope-from-date-input"
                  disabled={cascadeDialog.scope !== "from_date"}
                />
              </div>
            </div>
          </RadioGroup>

          <DialogFooter>
            <Button variant="outline"
              onClick={() => setCascadeDialog((s) => ({ ...s, open: false, pendingPayload: null }))}
              data-testid="cascade-cancel">Cancelar</Button>
            <Button className="bg-blue-700 hover:bg-blue-800"
              onClick={_confirmCascade}
              disabled={saving}
              data-testid="cascade-confirm">
              {saving ? "Aplicando..." : "Aplicar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Painel de simulação: mostra como o valor bruto será distribuído com 1, 2 ou 3 cirurgiões.
function FinancialSimulation({ bruto, bvsmRate, anesthRate }) {
  const fmt = (v) => (Number(v) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  const b = Number(bruto) || 0;
  const br = Math.min(Math.max(Number(bvsmRate) || 0, 0), 1);
  const ar = Math.min(Math.max(Number(anesthRate) || 0, 0), 1);
  const bvsm = b * br;
  const producao = b - bvsm;
  const anest = producao * ar;
  const cirurgioes = producao - anest;
  const perN = (n) => cirurgioes / Math.max(1, n);

  if (!b) {
    return (
      <div className="rounded-lg border-2 border-dashed border-slate-200 p-6 text-center text-sm text-slate-400">
        Informe o valor bruto SUS para ver a simulação da distribuição.
      </div>
    );
  }

  return (
    <div className="rounded-lg border-2 border-blue-200 bg-blue-50/40 p-5" data-testid="financial-simulation">
      <h4 className="font-display font-semibold text-blue-900 mb-3">Simulação da distribuição</h4>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <div className="bg-white rounded-md p-3 border border-slate-200">
          <div className="text-[10px] uppercase tracking-wide text-slate-500">Valor Bruto</div>
          <div className="text-base font-semibold text-slate-900 font-mono">{fmt(b)}</div>
        </div>
        <div className="bg-white rounded-md p-3 border border-amber-200">
          <div className="text-[10px] uppercase tracking-wide text-amber-700">BVSM ({(br * 100).toFixed(1)}%)</div>
          <div className="text-base font-semibold text-amber-700 font-mono">{fmt(bvsm)}</div>
        </div>
        <div className="bg-white rounded-md p-3 border border-purple-200">
          <div className="text-[10px] uppercase tracking-wide text-purple-700">Anestesista ({(ar * 100).toFixed(1)}%)</div>
          <div className="text-base font-semibold text-purple-700 font-mono">{fmt(anest)}</div>
        </div>
        <div className="bg-white rounded-md p-3 border border-emerald-200">
          <div className="text-[10px] uppercase tracking-wide text-emerald-700">Para cirurgiões</div>
          <div className="text-base font-semibold text-emerald-700 font-mono">{fmt(cirurgioes)}</div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        {[1, 2, 3].map((n) => (
          <div key={n} className="bg-white rounded-md p-3 border border-emerald-200/50">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700 mb-1">
              {n === 1 ? "1 cirurgião" : `${n} cirurgiões`}
            </div>
            <div className="text-lg font-bold text-emerald-700 font-mono">{fmt(perN(n))}</div>
            <div className="text-[10px] text-slate-400 mt-0.5">cada um recebe</div>
          </div>
        ))}
      </div>
    </div>
  );
}
